import { Editor } from '@tiptap/react';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { normalizeImportedDocxHtml } from '../../../utils/docxListNormalize';
import {
  DraftSignaturePlacement,
  LETTER_PAGE_HEIGHT_PX,
  LETTER_PAGE_WIDTH_PX,
  computeApprovedSignatureFitScale,
  sanitizeDraftSignaturePlacement,
} from '../signatureLayoutShared';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  ImageRun,
  Table as DocxTable,
  TableRow as DocxRow,
  TableCell as DocxCell,
  WidthType
} from 'docx';

/* ---------------- Export HTML ---------------- */
export function exportHtml(editor: Editor, title: string) {
  const body = editor.getHTML();
  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
body{font-family:Calibri,Arial,sans-serif;max-width:860px;margin:2rem auto;padding:0 1rem;line-height:1.5;color:#222}
h1,h2,h3,h4{font-family:Inter,Arial,sans-serif}
table{border-collapse:collapse}
table td,table th{border:1px solid #000;padding:6px 10px}
img{max-width:100%}
.page-break{page-break-after:always;border-top:1px dashed #bbb;margin:1rem 0}
blockquote{border-left:4px solid #ccc;padding-left:1rem;color:#555;margin-left:0}
pre{background:#f4f4f4;padding:.75rem 1rem;border-radius:6px;overflow:auto}
</style></head><body>${body}</body></html>`;
  saveAs(new Blob([html], { type: 'text/html;charset=utf-8' }), `${safeFile(title)}.html`);
}

/* ---------------- Export TXT ---------------- */
export function exportTxt(editor: Editor, title: string) {
  saveAs(new Blob([editor.getText()], { type: 'text/plain;charset=utf-8' }), `${safeFile(title)}.txt`);
}

/* ---------------- Print / PDF ---------------- */
export function printDoc(editor: Editor, title: string) {
  const body = editor.getHTML();
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
@page{margin:1in}
body{font-family:Calibri,Arial,sans-serif;line-height:1.5;color:#000}
h1,h2,h3,h4{font-family:Inter,Arial,sans-serif}
table{border-collapse:collapse;width:100%}
table td,table th{border:1px solid #000;padding:6px 10px}
img{max-width:100%}
.page-break{page-break-after:always;border:0;height:0}
blockquote{border-left:4px solid #888;padding-left:1rem;color:#444}
pre{background:#f4f4f4;padding:.5rem;border-radius:4px}
</style></head><body>${body}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
  }, 250);
}

/**
 * Download the paginated preview as PDF.
 *
 * Renders N independent A4-sized "page sheets" (one per preview page)
 * into a hidden offscreen container, captures each with html2canvas,
 * and assembles a multi-page PDF via jsPDF. Triggers an automatic
 * file download — no print dialog, no popup window.
 */
const PDF_MM_TO_PX = (mm: number) => (mm * 96) / 25.4;
// Must match CONTENT_VCLEARANCE_MM in EditorSurface/PreviewPane so the
// exported page's content band lines up with the editor's pagination and
// keeps a breathing gap from the letterhead/footer art.
const CONTENT_VCLEARANCE_MM = 3;

interface SignaturePlacementConfig {
  markAllPages: boolean;
  base: DraftSignaturePlacement;
  overrides: Record<string, DraftSignaturePlacement>;
  skippedPages?: number[];
}

const DEFAULT_SIGNATURE_PLACEMENT: DraftSignaturePlacement = {
  pageIndex: 0,
  x: 56,
  y: 850,
  width: 168,
  height: 140,
};

export function downloadPdf(
  editor: Editor,
  opts: {
    title: string;
    referenceNo: string;
    displayDate: string;
    pageWmm: number;
    pageHmm: number;
    pageCount: number;
    /** Letterhead height in CSS px (rendered at the page's logical width). */
    letterheadSafePx: number;
    /** Footer image height in CSS px (rendered at the page's logical width). */
    footerSafePx?: number;
    /** Optional QR image data URL to render in the footer. */
    qrCodeData?: string | null;
    /** Approved signature HTML to render over the exported pages. */
    approvedSignatureHtml?: string | null;
    /** Signature placement metadata. */
    signaturePlacementConfig?: SignaturePlacementConfig | null;
    /** Visual gap between page tiles in px (matches the editor; default 24). */
    pageGapPx?: number;
    /** Optional inner margin used for print-safe blobs, in millimetres. */
    printSafeMarginMm?: number;
    /**
     * When true, {@link runPdfExport} RESOLVES with the generated PDF Blob
     * instead of triggering a download. Consumed by {@link exportPdfBlob} so
     * the read-only viewer / approval pages can build a File (for share or ZIP
     * bundles) from the exact same editor-accurate renderer the draft uses.
     */
    returnBlob?: boolean;
  }
) {
  // Wait two animation frames so any pending Pagination plugin update
  // has committed to the DOM before we serialise the body.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    void (editor.view?.dom as HTMLElement | undefined)?.offsetHeight;
    runPdfExport(editor, opts).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('PDF export failed:', err);
    });
  }));
}

/**
 * Editor-accurate PDF export that RESOLVES with the generated Blob instead of
 * saving to disk. Mirrors {@link downloadPdf} exactly (same renderer, same
 * pagination, same letterhead/footer/signature handling) so the read-only
 * viewer (ViewLettersPage) and approval (PendingApprovalPage) screens produce
 * output identical to the draft download. The caller owns the filename and any
 * File wrapping.
 */
export function exportPdfBlob(
  editor: Editor,
  opts: Parameters<typeof downloadPdf>[1]
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    // Same two-frame settle as downloadPdf so pagination has committed before
    // we serialise the body.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      void (editor.view?.dom as HTMLElement | undefined)?.offsetHeight;
      runPdfExport(editor, { ...opts, returnBlob: true })
        .then((blob) => {
          if (blob instanceof Blob) {
            resolve(blob);
          } else {
            reject(new Error('PDF blob generation returned no data'));
          }
        })
        .catch(reject);
    }));
  });
}

async function runPdfExport(
  editor: Editor,
  opts: Parameters<typeof downloadPdf>[1]
): Promise<Blob | void> {
  // Use the editor's *rendered* DOM (including pagination spacer widgets)
  // verbatim — this is the exact same source PreviewPane renders. Combined
  // with the .ProseMirror class on the content node (so it inherits the
  // real editor CSS) and the editor CSS variables defined on the export
  // root, the exported pages match the on-screen preview pixel-for-pixel.
  const body = editor.view?.dom?.innerHTML ?? editor.getHTML();

  const {
    title,
    referenceNo,
    displayDate,
    pageWmm,
    pageHmm,
    letterheadSafePx,
    footerSafePx = 0,
    qrCodeData = null,
    approvedSignatureHtml = null,
    signaturePlacementConfig = null,
    pageGapPx = 24,
    printSafeMarginMm = 0
  } = opts;

  // Recompute pageCount from the LIVE editor DOM height now that pagination
  // has settled — the caller's React-state value may be from a prior pass.
  // IMPORTANT: never drop below the preview's computed count (opts.pageCount).
  // PreviewPane derives its count as max(stridePages, fitPages); the stride-only
  // recompute here can UNDERCOUNT and silently drop a trailing page — and with
  // it any signature placed on that page (e.g. a "mark sign on all pages"
  // override on the last page). Take the max so the PDF always has at least as
  // many pages as the on-screen preview.
  const stridePx = PDF_MM_TO_PX(pageHmm) + pageGapPx;
  let pageCount = opts.pageCount;
  const liveDom = editor.view?.dom as HTMLElement | undefined;
  if (liveDom) {
    const h = liveDom.scrollHeight;
    const pages = Math.max(1, Math.floor((h - 2) / stridePx) + 1);
    pageCount = Math.max(opts.pageCount, pages);
  }

  const safeTitle = escapeHtml(title);
  const safeRef = escapeHtml(referenceNo);
  const safeDate = escapeHtml(displayDate);

  const letterheadSafeMm = (letterheadSafePx * 25.4) / 96;
  // SEPL band sits 6mm below the letterhead bottom and is 8mm tall.
  const seplTopMm = letterheadSafeMm + 6;
  const SEPL_BAND_HEIGHT_MM = 8;
  const CONTENT_LEFT_MM = 15;
  const CONTENT_RIGHT_MM = 15;

  const getPlacementForPage = (pageIndex: number): DraftSignaturePlacement | null => {
    if (!signaturePlacementConfig) return null;
    const maxIndex = Math.max(0, pageCount - 1);
    const base = sanitizeDraftSignaturePlacement(signaturePlacementConfig.base, DEFAULT_SIGNATURE_PLACEMENT);
    const normalizedBase = {
      ...base,
      pageIndex: signaturePlacementConfig.markAllPages
        ? Math.min(Math.max(0, Number(base.pageIndex) || 0), maxIndex)
        : maxIndex,
    };

    if (signaturePlacementConfig.markAllPages && signaturePlacementConfig.skippedPages?.includes(pageIndex)) return null;
    if (!signaturePlacementConfig.markAllPages) {
      return normalizedBase.pageIndex === pageIndex ? normalizedBase : null;
    }

    const override = signaturePlacementConfig.overrides?.[String(pageIndex)];
    return override ? { ...sanitizeDraftSignaturePlacement(override, DEFAULT_SIGNATURE_PLACEMENT), pageIndex } : { ...normalizedBase, pageIndex };
  };

  const signatureHtmlForPage = (pageIndex: number) => {
    const placement = getPlacementForPage(pageIndex);
    if (!placement) return '';
    const posStyle = `left:${(placement.x / LETTER_PAGE_WIDTH_PX) * 100}%;top:${(placement.y / LETTER_PAGE_HEIGHT_PX) * 100}%;width:${(placement.width / LETTER_PAGE_WIDTH_PX) * 100}%;height:${(placement.height / LETTER_PAGE_HEIGHT_PX) * 100}%`;
    if (approvedSignatureHtml) {
      // Scale the signature to FILL the placement box (anchored at its top-left),
      // matching LetterDocument/QR. Without this the natural-size signature sits
      // top-left in an oversized box and looks shifted "too high".
      const fitScale = computeApprovedSignatureFitScale(approvedSignatureHtml, placement);
      const anchorStyle = `left:${(placement.x / LETTER_PAGE_WIDTH_PX) * 100}%;top:${(placement.y / LETTER_PAGE_HEIGHT_PX) * 100}%;transform:scale(${fitScale});transform-origin:top left`;
      return `<div class="pdf-approved-signature" style="${anchorStyle}">${approvedSignatureHtml}</div>`;
    }
    // Draft (not yet signed): render the SAME placeholder box the editor's
    // PreviewPane shows, so the draft preview/PDF matches the editor instead of
    // silently omitting the signature box the user positioned.
    return `<div class="pdf-draft-signature" style="${posStyle}">Signature Box</div>`;
  };

  const sheetHtml = (i: number) => {
    const isFirst = i === 0;
    // .pdf-content-window now starts at sheet y = letterheadSafePx, so
    // a flow positioned at top=0 already aligns to the content-area
    // top. For pages > 0 we shift up by i*stridePx so the i-th content
    // band lands at the visible top of the window.
    const topPx = -i * stridePx;
    const letterheadSrc = isFirst
      ? `${location.origin}/letterhead.png`
      : `${location.origin}/letterhead2.png`;
    return `<section class="pdf-page${isFirst ? ' first' : ''}">
        <div class="pdf-content-window">
          <div class="pdf-content-flow ProseMirror" style="top: ${topPx}px">${body}</div>
        </div>
        <img class="pdf-letterhead" src="${letterheadSrc}" alt="" />
        ${
          isFirst
            ? `<div class="pdf-sepl-band">
                 <span>SEPL reference number: ${safeRef}</span>
                 <span>Date: ${safeDate}</span>
               </div>`
            : ''
        }
        <img class="pdf-footer" src="${location.origin}/footer.png" alt="" />
        ${qrCodeData ? `<div class="pdf-footer-qr"><img src="${qrCodeData}" alt="QR Code" /></div>` : ''}
        ${signatureHtmlForPage(i)}
        <span class="pdf-page-num${qrCodeData ? ' with-footer-qr' : ''}">${i + 1}</span>
      </section>`;
  };
  const sheets = Array.from({ length: Math.max(1, pageCount) }, (_, i) => sheetHtml(i)).join('\n');

  // Build an offscreen container in the *current* document and render
  // all sheets there. The container is positioned far off-screen so the
  // user never sees it, but it is part of the live layout, which is
  // required for html2canvas to capture it correctly.
  const css = `
.pdf-export-root {
  position: fixed;
  left: -10000px;
  top: 0;
  z-index: -1;
  pointer-events: none;
  background: #fff;
  font-family: 'Carlito', 'Calibri', 'Segoe UI', Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.15;
  color: #222;
  /* Editor CSS custom properties (light theme) — these are normally
   * defined on .sepl-word-editor, but the export root lives outside that
   * subtree (appended to <body>). Re-declaring them here lets the .ProseMirror
   * content node inherit the EXACT editor styling (borders, accents, fonts)
   * instead of the drifted hand-written copies used previously. */
  --bg: #f3f3f3;
  --bg-elev: #ffffff;
  --bg-soft: #fafafa;
  --fg: #1f1f1f;
  --fg-mute: #555;
  --border: #d8d8d8;
  --border-strong: #b6b6b6;
  --accent: #185abd;
  --accent-fg: #ffffff;
  --accent-soft: #e7f0fb;
  --danger: #c5221f;
  --success: #1e8e3e;
  --radius: 6px;
  --radius-sm: 4px;
  --paper: #ffffff;
  --paper-edge: #e0e0e0;
  --font-ui: 'Inter', 'Segoe UI', Arial, sans-serif;
  --font-paper: 'Carlito', 'Calibri', 'Segoe UI', Arial, sans-serif;
}
.pdf-export-root, .pdf-export-root * { box-sizing: border-box; }

/* Each sheet is a real ${pageWmm}mm x ${pageHmm}mm page. */
.pdf-export-root .pdf-page {
  width: ${pageWmm}mm;
  height: ${pageHmm}mm;
  position: relative;
  overflow: hidden;
  background: #fff;
}

/* Content layer — anchors to safe zones without hard clipping.
 * Allows full content visibility without cutting edges. */
.pdf-export-root .pdf-content-window {
  position: absolute;
  top: ${Math.max(0, letterheadSafePx + PDF_MM_TO_PX(CONTENT_VCLEARANCE_MM))}px;
  bottom: ${Math.max(0, footerSafePx + PDF_MM_TO_PX(CONTENT_VCLEARANCE_MM))}px;
  left: 0;
  right: 0;
  overflow: visible;
  z-index: 1;
}
.pdf-export-root .pdf-content-flow {
  position: absolute;
  left: ${CONTENT_LEFT_MM}mm;
  width: calc(${pageWmm}mm - ${CONTENT_LEFT_MM}mm - ${CONTENT_RIGHT_MM}mm);
  /* top set inline per page; relative to .pdf-content-window which now
   * starts at sheet y = letterheadSafePx, so flow y=0 aligns to that
   * top when topPx = -i*stridePx. */
  font-family: var(--font-paper);
  font-size: 11pt;
  line-height: 1.15;
  color: #222;
  box-sizing: border-box;
  /* The node also carries the .ProseMirror class to inherit editor styling;
   * neutralise .ProseMirror's positioning/min-height so the per-page slice
   * math (absolute + inline top offset) keeps working. */
  min-height: 0 !important;
  max-width: none;
}
/* Page-1-only SEPL meta-band reservation matches the editor's
 * .ProseMirror > :first-child rule. */
.pdf-export-root .pdf-content-flow > :first-child:not(.pm-page-spacer),
.pdf-export-root .pdf-content-flow > .pm-page-spacer:first-child + :not(.pm-page-spacer) {
  padding-top: 14mm;
}

/* Body content (paragraphs, headings, lists, blockquote, code, marks) and
 * ALL table styling are now provided by the real editor CSS via the
 * .ProseMirror class on .pdf-content-flow — no hand-written copies here, so
 * the export can never drift out of sync with the editor/preview again. */

/* Chrome layers — opaque, z-index above content. */
.pdf-export-root .pdf-letterhead {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  display: block;
  z-index: 5;
  background: #fff;
}
.pdf-export-root .pdf-sepl-band {
  position: absolute;
  top: ${seplTopMm}mm;
  left: ${CONTENT_LEFT_MM}mm;
  right: ${CONTENT_RIGHT_MM}mm;
  height: ${SEPL_BAND_HEIGHT_MM}mm;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8mm;
  padding: 0;
  background: #fff;
  font-size: 10pt;
  font-weight: 600;
  color: #1b1b1b;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}
.pdf-export-root .pdf-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: auto;
  display: block;
  transform: translateY(0);
  background: #fff;
  z-index: 5;
}
.pdf-export-root .pdf-page-num {
  position: absolute;
  right: 12mm;
  bottom: 6mm;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 0 0 1px rgba(17, 24, 39, 0.08);
  font-size: 9pt;
  line-height: 1;
  color: #555;
  font-variant-numeric: tabular-nums;
  z-index: 7;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}
.pdf-export-root .pdf-page-num.with-footer-qr {
  right: 108px;
  /* 40px lower than original (was footerSafePx + 10) per user request. */
  bottom: ${footerSafePx - 30}px;
  z-index: 9;
}
.pdf-export-root .pdf-footer-qr {
  position: absolute;
  right: 28px;
  /* 40px lower than original (was footerSafePx - 8) per user request. */
  bottom: ${footerSafePx - 48}px;
  width: 64px;
  height: 64px;
  padding: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  z-index: 8;
}
.pdf-export-root .pdf-footer-qr img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.pdf-export-root .pdf-approved-signature {
  position: absolute;
  z-index: 30;
  overflow: visible;
  pointer-events: none;
}
.pdf-export-root .pdf-approved-signature > * {
  max-width: none !important;
  /* Pin the inline-block signature root to the top of its line box (= the box
   * top the user placed) instead of letting baseline strut math push it down.
   * The offset is otherwise sensitive to the inherited font-size/line-height,
   * which differs between this PDF context and the on-screen preview and is
   * what made the approved signature sit slightly low. */
  vertical-align: top;
}
.pdf-export-root .pdf-draft-signature {
  position: absolute;
  z-index: 30;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #d1d5db;
  border-radius: 6px;
  background: rgba(249, 250, 251, 0.95);
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
}

/* Spacer widgets stay in flow so slice math matches the editor. */
.pdf-export-root .pm-page-spacer {
  pointer-events: none;
  user-select: none;
}

/* Editor's per-page chrome (when leaked into cloned innerHTML) hidden. */
.pdf-export-root .pdf-content-flow .page-letterhead,
.pdf-export-root .pdf-content-flow .page-letterhead-meta,
.pdf-export-root .pdf-content-flow .page-footer,
.pdf-export-root .pdf-content-flow .page-number,
.pdf-export-root .pdf-content-flow .we-table-select-all,
.pdf-export-root .pdf-content-flow .we-table-resize-grip,
.pdf-export-root .pdf-content-flow .column-resize-handle,
.pdf-export-root .pdf-content-flow .ProseMirror-gapcursor { display: none !important; }
/* Neutralise the editor's selected-cell blue overlay if a cell happened to
 * be selected at export time. */
.pdf-export-root .pdf-content-flow .selectedCell::after { display: none !important; }

.pdf-export-root .pdf-content-flow img { max-width: 100%; height: auto; }
.pdf-export-root .pdf-content-flow .page-break {
  /* Keep the SAME 24px flow height as the editor (ribbon.css) so an explicit
   * page break lands the following content identically on screen and in the
   * PDF; just make it invisible (no dashed band / label in the export). */
  height: 24px;
  margin: 0;
  padding: 0;
  border: 0;
  box-sizing: border-box;
  visibility: hidden;
}
`;

  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-pdf-export', '1');
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  const root = document.createElement('div');
  root.className = 'pdf-export-root';
  root.innerHTML = sheets;
  document.body.appendChild(root);

  try {
    // Wait for every <img> in the offscreen tree to finish loading.
    const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalHeight > 0) {
              resolve();
              return;
            }
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          })
      )
    );

    // jsPDF page setup matches sheet size in millimetres.
    const orientation = pageWmm > pageHmm ? 'l' : 'p';
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format: [pageWmm, pageHmm],
      compress: true
    });

    const pageEls = Array.from(root.querySelectorAll<HTMLElement>('.pdf-page'));
    const safeMarginMm = Math.max(0, Math.min(printSafeMarginMm, Math.min(pageWmm, pageHmm) / 4));
    const safeRenderWmm = pageWmm - (safeMarginMm * 2);
    const safeRenderHmm = pageHmm;
    const safeRenderXmm = safeMarginMm;
    const safeRenderYmm = 0;
    // Render each sheet with html2canvas at 2x for crisp text.
    const captureScale = 2;
    for (let i = 0; i < pageEls.length; i++) {
      const el = pageEls[i];
      const canvas = await html2canvas(el, {
        scale: captureScale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: el.offsetWidth,
        windowHeight: el.offsetHeight
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      if (i > 0) {
        pdf.addPage([pageWmm, pageHmm], orientation);
      }
      pdf.addImage(imgData, 'JPEG', safeRenderXmm, safeRenderYmm, safeRenderWmm, safeRenderHmm, undefined, 'FAST');
    }

    if (opts.returnBlob) {
      // Caller wants the bytes (e.g. read-only viewer building a File for
      // share / ZIP bundles). Generate the Blob synchronously before the
      // `finally` cleanup tears down the offscreen render root.
      return pdf.output('blob');
    }
    pdf.save(`${safeFile(title)}.pdf`);
  } finally {
    if (root.parentNode) root.parentNode.removeChild(root);
    if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
  }
}

/* ---------------- Export .docx ---------------- */
export async function exportDocx(editor: Editor, title: string) {
  const root = document.createElement('div');
  root.innerHTML = editor.getHTML();
  const children = await nodesToDocx(Array.from(root.childNodes));
  const doc = new Document({
    creator: 'SEPL Letter Editor',
    title,
    sections: [{ properties: {}, children }]
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${safeFile(title)}.docx`);
}

async function nodesToDocx(nodes: ChildNode[]): Promise<Paragraph[]> {
  const out: Paragraph[] = [];
  for (const n of nodes) {
    const p = await elementToParagraphs(n);
    out.push(...p);
  }
  return out;
}

async function elementToParagraphs(node: ChildNode): Promise<Paragraph[]> {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = node.textContent || '';
    if (!t.trim()) return [];
    return [new Paragraph({ children: [new TextRun(t)] })];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return [];
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  // Headings
  const headingMap: Record<string, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
    h1: HeadingLevel.HEADING_1,
    h2: HeadingLevel.HEADING_2,
    h3: HeadingLevel.HEADING_3,
    h4: HeadingLevel.HEADING_4,
    h5: HeadingLevel.HEADING_5,
    h6: HeadingLevel.HEADING_6
  };
  if (headingMap[tag]) {
    return [
      new Paragraph({
        heading: headingMap[tag],
        alignment: alignFromStyle(el),
        children: inlineRuns(el)
      })
    ];
  }

  if (tag === 'p') {
    return [
      new Paragraph({
        alignment: alignFromStyle(el),
        children: inlineRuns(el)
      })
    ];
  }
  if (tag === 'br') {
    return [new Paragraph({ children: [new TextRun({ text: '', break: 1 })] })];
  }
  if (tag === 'hr' || el.classList.contains('page-break')) {
    return [new Paragraph({ children: [new TextRun({ text: '', break: 1 })], pageBreakBefore: true })];
  }
  if (tag === 'blockquote') {
    return [new Paragraph({ children: inlineRuns(el), indent: { left: 720 } })];
  }
  if (tag === 'pre') {
    return [new Paragraph({ children: [new TextRun({ text: el.textContent || '', font: 'Consolas' })] })];
  }
  if (tag === 'ul' || tag === 'ol') {
    const items: Paragraph[] = [];
    for (const li of Array.from(el.children)) {
      items.push(
        new Paragraph({
          children: inlineRuns(li as HTMLElement),
          bullet: tag === 'ul' ? { level: 0 } : undefined,
          numbering: tag === 'ol' ? { reference: 'num-default', level: 0 } : undefined
        })
      );
    }
    return items;
  }
  if (tag === 'table') {
    return [tableToDocx(el as HTMLTableElement) as unknown as Paragraph];
  }
  if (tag === 'img') {
    const para = await imgToParagraph(el as HTMLImageElement);
    return para ? [para] : [];
  }
  // Fallback: recurse children
  const acc: Paragraph[] = [];
  for (const c of Array.from(el.childNodes)) {
    acc.push(...(await elementToParagraphs(c)));
  }
  return acc;
}

function inlineRuns(el: HTMLElement, inherited: Record<string, unknown> = {}): TextRun[] {
  const runs: TextRun[] = [];
  const walk = (node: ChildNode, fmt: Record<string, unknown>) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!text) return;
      runs.push(new TextRun({ text, ...fmt }));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const e = node as HTMLElement;
    const next: Record<string, unknown> = { ...fmt };
    const t = e.tagName.toLowerCase();
    if (t === 'strong' || t === 'b') next.bold = true;
    if (t === 'em' || t === 'i') next.italics = true;
    if (t === 'u') next.underline = {};
    if (t === 's' || t === 'strike' || t === 'del') next.strike = true;
    if (t === 'sub') next.subScript = true;
    if (t === 'sup') next.superScript = true;
    const color = e.style.color;
    if (color) next.color = rgbToHex(color);
    const fs = e.style.fontSize;
    if (fs) {
      const num = parseFloat(fs);
      if (!isNaN(num)) next.size = Math.round(num * 2); // half-points
    }
    const ff = e.style.fontFamily;
    if (ff) next.font = ff.replace(/['"]/g, '').split(',')[0].trim();
    const hl = e.style.backgroundColor;
    if (hl) next.highlight = 'yellow';
    for (const child of Array.from(e.childNodes)) walk(child, next);
  };
  for (const c of Array.from(el.childNodes)) walk(c, inherited);
  return runs.length ? runs : [new TextRun('')];
}

function alignFromStyle(el: HTMLElement): (typeof AlignmentType)[keyof typeof AlignmentType] | undefined {
  const a = el.style.textAlign;
  if (a === 'center') return AlignmentType.CENTER;
  if (a === 'right') return AlignmentType.RIGHT;
  if (a === 'justify') return AlignmentType.JUSTIFIED;
  if (a === 'left') return AlignmentType.LEFT;
  return undefined;
}

function tableToDocx(el: HTMLTableElement): DocxTable {
  const rows: DocxRow[] = [];
  for (const tr of Array.from(el.rows)) {
    const cells: DocxCell[] = [];
    for (const td of Array.from(tr.cells)) {
      cells.push(
        new DocxCell({
          children: [new Paragraph({ children: inlineRuns(td) })],
          width: { size: 100 / tr.cells.length, type: WidthType.PERCENTAGE }
        })
      );
    }
    rows.push(new DocxRow({ children: cells }));
  }
  return new DocxTable({ rows, width: { size: 100, type: WidthType.PERCENTAGE } });
}

async function imgToParagraph(img: HTMLImageElement): Promise<Paragraph | null> {
  try {
    const src = img.src;
    let buf: ArrayBuffer;
    if (src.startsWith('data:')) {
      const b64 = src.split(',')[1];
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      buf = arr.buffer;
    } else {
      const res = await fetch(src);
      buf = await res.arrayBuffer();
    }
    const w = img.naturalWidth || 400;
    const h = img.naturalHeight || 300;
    const maxW = 500;
    const scale = w > maxW ? maxW / w : 1;
    return new Paragraph({
      children: [
        new ImageRun({
          data: buf,
          transformation: { width: Math.round(w * scale), height: Math.round(h * scale) }
        } as ConstructorParameters<typeof ImageRun>[0])
      ]
    });
  } catch {
    return null;
  }
}

function rgbToHex(rgb: string): string {
  const m = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!m) return rgb.replace('#', '');
  return [m[1], m[2], m[3]].map((n) => parseInt(n).toString(16).padStart(2, '0')).join('');
}

/* ---------------- Import .docx / .html / .txt ---------------- */
export async function importFile(editor: Editor, file: File): Promise<string | null> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.docx')) {
    const buf = await file.arrayBuffer();
    const res = await mammoth.convertToHtml({ arrayBuffer: buf });
    // Merge consecutive sibling <ol>/<ul> elements so Word numbered lists
    // keep their original 1, 2, 3, ... sequence (mammoth otherwise often
    // emits one <ol> per item, restarting the count at 1 every time).
    const normalized = normalizeImportedDocxHtml(res.value || '');
    editor.commands.setContent(normalized, true);
    return file.name.replace(/\.docx$/i, '');
  }
  if (name.endsWith('.html') || name.endsWith('.htm')) {
    const text = await file.text();
    editor.commands.setContent(text, true);
    return file.name.replace(/\.html?$/i, '');
  }
  if (name.endsWith('.txt')) {
    const text = await file.text();
    const html = text
      .split(/\n\n+/)
      .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
      .join('');
    editor.commands.setContent(html, true);
    return file.name.replace(/\.txt$/i, '');
  }
  alert('Unsupported file type. Use .docx, .html, or .txt');
  return null;
}

/* ---------------- helpers ---------------- */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function safeFile(s: string): string {
  return (s || 'document').replace(/[\\/:*?"<>|]+/g, '_').trim() || 'document';
}
